"use client";

/**
 * <AnalyticsTracker> — 사용자 결정 #18: 행동 추적 시스템.
 *
 * 동작:
 *   1) pathname 바뀔 때마다 pageview 이벤트
 *   2) 페이지마다 dwell tracker 생성 — visibility hidden / unmount 시 flush
 *   3) document-wide click delegate — [data-track] 버튼 클릭 자동 추적
 *
 * 배치: AppProviders 안 (모든 페이지에서 활성)
 */
import { useEffect, useLayoutEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { trackPageview, trackClick, createDwellTracker, flushNow } from "@/lib/analytics";
import { useConsent } from '@/contexts/consent-context';
import { isBiteMeasurementPath, startBitePage, resetBiteMeasurement } from '@/lib/bite-measurement';
import {startBiteUsage} from '@/lib/bite-usage-totals';
import {startBiteCampaignSource} from '@/lib/bite-campaign-source';
import {countBiteArrival} from '@/lib/bite-conversion';
import {startBiteGa4,stopBiteGa4} from '@/lib/bite-ga4';
import {biteActionLabel} from '@/lib/bite-action-label';
import {initializeConsent} from '@/lib/consent';
import {allowsBiteGa4} from '@/lib/bite-ga4-policy';

export function AnalyticsTracker() {
  const pathname = usePathname() || "/";
  const { consent } = useConsent();
  const trackerRef = useRef<ReturnType<typeof createDwellTracker> | null>(null);
  useEffect(()=>{
    if(!isBiteMeasurementPath(pathname))return;
    let active=true;
    const sync=()=>void initializeConsent().then(()=>{if(active){if(allowsBiteGa4())void startBiteGa4(pathname);else stopBiteGa4();}});
    sync();window.addEventListener('bite-consent-change',sync);
    return()=>{active=false;window.removeEventListener('bite-consent-change',sync);};
  },[pathname,consent.analytics,consent.ts]);
  useLayoutEffect(()=>{if(isBiteMeasurementPath(pathname))countBiteArrival();},[pathname]);
  useLayoutEffect(()=>{
    if(isBiteMeasurementPath(pathname))return startBiteCampaignSource(pathname);
  },[pathname]);

  useLayoutEffect(()=>{
    if(isBiteMeasurementPath(pathname))return startBiteUsage(pathname);
  },[pathname]);

  // pathname 바뀔 때마다 이전 dwell flush + 새 페이지 pageview/dwell 시작
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    if (isBiteMeasurementPath(pathname)) {
      if (consent.analytics) return startBitePage(pathname);
      if (consent.ts > 0) resetBiteMeasurement();
      return;
    }
    // 이전 트래커 종료
    if (trackerRef.current) trackerRef.current.flush();
    // 새 페이지 시작
    trackPageview(pathname);
    trackerRef.current = createDwellTracker(pathname);
    // visibility hidden 시 dwell flush + 큐 잔여분 flush
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        if (trackerRef.current) {
          trackerRef.current.flush();
          trackerRef.current = null;
        }
        flushNow(); // 큐 잔여 batch 즉시 sendBeacon
      }
    };
    const onUnload = () => {
      if (trackerRef.current) trackerRef.current.flush();
      trackerRef.current = null;
      flushNow();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onUnload);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onUnload);
      if (trackerRef.current) {
        trackerRef.current.flush();
        trackerRef.current = null;
      }
    };
  }, [pathname, consent.analytics, consent.ts]);

  // 자동 클릭 추적 — [data-track="라벨"] 또는 button[data-track]
  // [data-no-track] 또는 부모에 data-no-track 있으면 제외
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const bite=isBiteMeasurementPath(window.location.pathname);
      const trackedEl = target.closest(bite?'[data-track],button,a,[role="button"]':"[data-track]") as HTMLElement | null;
      if (!trackedEl) return;
      if (trackedEl.closest("[data-no-track]")) return;
      const label = bite?biteActionLabel(trackedEl):trackedEl.dataset.track || "(no-label)";
      const metaJson = trackedEl.dataset.trackMeta;
      let meta: Record<string, unknown> | undefined;
      if (metaJson) {
        try { meta = JSON.parse(metaJson); } catch { /* ignore */ }
      }
      trackClick(label, meta);
    };
    document.addEventListener("click", onClick);
    const onChange=(event:Event)=>{
      if(!isBiteMeasurementPath(window.location.pathname))return;
      const element=event.target as HTMLElement;
      if(!element.matches('select,input[type="checkbox"],input[type="radio"]')||element.closest('[data-no-track]'))return;
      if(element.tagName!=='SELECT'&&element.closest('[data-track],[role="button"]'))return;
      trackClick(biteActionLabel(element));
    };
    document.addEventListener('change',onChange);
    return () => {document.removeEventListener("click", onClick);document.removeEventListener('change',onChange);};
  }, []);

  return null;
}
